import { CSSProperties, cloneElement } from "react";
import TypographyText from "../TypographyText/TypographyText";
import { UnstyledLink } from "../UnstyledLink/UnstyledLink";

//NB. This component combines the concepts of Badge and Tag from the design system in figma.

export type BadgeColor = "brand" | "success" | "error" | "warning" | "info" | "gray" | "blue" | "red" | "green" | "yellow";

export type BadgeProps = {
  text: string;
  dot?: boolean;
  rounded?: boolean;
  color?: BadgeColor;
  iconBefore?: React.JSX.Element;
  iconAfter?: React.JSX.Element;
  onClick?: () => void;
  link?: string;
  width?: string;
  size?: "xs" | "sm" | "md" | "lg";
  css?: CSSProperties;
  ref?: React.RefObject<HTMLDivElement>;
};

export default function Badge({
  size = "sm",
  text,
  dot = false,
  rounded = false,
  color = "info",
  iconBefore,
  iconAfter,
  css,
  onClick,
  ref,
  link,
  width,
}: BadgeProps) {
  //minus paddingY and border
  const heightMap = {
    xs: 12,
    sm: 18,
    md: 20,
    lg: 28,
  };
  //minus border
  const paddingXMap = {
    xs: 4,
    sm: 5,
    md: 7,
    lg: 9,
  };
  const paddingYMap = {
    xs: 1,
    sm: 2,
    md: 2,
    lg: 4,
  };

  const dotColor = `var(--${color}-500)`;
  const iconColor = `var(--${color}-600)`;
  const textColor = `var(--${color}-700)`;
  const backgroundColor = color === "info" ? "white" : `var(--${color}-50)`;
  const borderColor = color === "info" ? "var(--gray-300)" : `var(--${color}-200)`;
  const iconSize = size === "sm" || size === "xs" ? 12 : 16;
  const cursor = onClick ? "pointer" : undefined;

  if (iconAfter) {
    iconAfter = cloneElement(iconAfter, { width: iconSize, height: iconSize, color: iconColor });
  }
  if (iconBefore) {
    iconBefore = cloneElement(iconBefore, { width: iconSize, height: iconSize, color: iconColor });
  }

  const content = (
    <div
      style={{
        height: heightMap[size],
        width: "fit-content",
        border: `1px solid ${borderColor}`,
        backgroundColor: backgroundColor,
        borderRadius: rounded ? "16px" : "4px",
        display: "flex",
        alignItems: "center",
        alignContent: "center",
        gap: "4px",
        padding: `${paddingYMap[size]}px ${paddingXMap[size]}px`,
        whiteSpace: "nowrap",
        boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
        cursor,
        ...css,
      }}
      onClick={onClick}
      ref={ref}
    >
      {!dot && iconBefore}
      {dot && !iconBefore && (
        <div
          style={{
            height: "8px",
            width: "8px",
            borderRadius: "50%",
            backgroundColor: dotColor,
            cursor,
          }}
        />
      )}
      <TypographyText size={size === "sm" || size === "xs" ? "xs" : "sm"} weight="medium" css={{ color: textColor, cursor }} width={width} truncate>
        {text}
      </TypographyText>
      {iconAfter}
    </div>
  );

  if (link) {
    return <UnstyledLink to={link}>{content}</UnstyledLink>;
  } else {
    return content;
  }
}
